// A/B Testing data layer for Max OS Cloud
// Includes: test templates, localStorage persistence, statistical helpers, CRUD

const now = Date.now();
const day = 86400000;

const STORAGE_KEY = 'maxemployee.abTests';

// ─── Statistical Helpers ───

/**
 * Two-proportion z-test confidence calculation
 * Returns confidence level 0-99
 */
export function calculateConfidence(convA, impA, convB, impB) {
  if (impA === 0 || impB === 0) return 0;
  const pA = convA / impA;
  const pB = convB / impB;
  const pPool = (convA + convB) / (impA + impB);
  if (pPool === 0 || pPool === 1) return 0;
  const se = Math.sqrt(pPool * (1 - pPool) * (1 / impA + 1 / impB));
  if (se === 0) return 0;
  const z = (pB - pA) / se;
  const absZ = Math.abs(z);
  if (absZ < 1) return Math.round(absZ * 50);
  if (absZ < 1.64) return Math.round(50 + (absZ - 1) * 30);
  if (absZ < 1.96) return Math.round(75 + (absZ - 1.64) * 20);
  if (absZ < 2.58) return Math.round(90 + (absZ - 1.96) * 8);
  return Math.min(99, Math.round(95 + (absZ - 2.58) * 2));
}

/**
 * Calculate p-value from z-score (two-tailed)
 */
export function calculatePValue(convA, impA, convB, impB) {
  if (impA === 0 || impB === 0) return 1;
  const pA = convA / impA;
  const pB = convB / impB;
  const pPool = (convA + convB) / (impA + impB);
  if (pPool === 0 || pPool === 1) return 1;
  const se = Math.sqrt(pPool * (1 - pPool) * (1 / impA + 1 / impB));
  if (se === 0) return 1;
  const z = Math.abs((pB - pA) / se);
  // Approximation of two-tailed p-value
  const p = 2 * (1 - normalCDF(z));
  return Math.max(0.001, Math.round(p * 1000) / 1000);
}

function normalCDF(x) {
  // Approximation of standard normal CDF
  const a1 = 0.254829592, a2 = -0.284496736, a3 = 1.421413741;
  const a4 = -1.453152027, a5 = 1.061405429, p = 0.3275911;
  const sign = x < 0 ? -1 : 1;
  x = Math.abs(x) / Math.sqrt(2);
  const t = 1 / (1 + p * x);
  const y = 1 - (((((a5 * t + a4) * t) + a3) * t + a2) * t + a1) * t * Math.exp(-x * x);
  return 0.5 * (1 + sign * y);
}

/**
 * Sample size calculator
 * Returns required sample size per variant for given parameters
 */
export function calculateSampleSize(baselineRate, mde, confidence = 95, power = 80) {
  if (baselineRate <= 0 || baselineRate >= 1 || mde <= 0) return 0;
  const zAlpha = confidence === 99 ? 2.576 : confidence === 95 ? 1.96 : 1.645;
  const zBeta = power === 90 ? 1.282 : power === 80 ? 0.842 : 1.645;
  const p1 = baselineRate;
  const p2 = baselineRate * (1 + mde / 100);
  const pAvg = (p1 + p2) / 2;
  const numerator = (zAlpha * Math.sqrt(2 * pAvg * (1 - pAvg)) + zBeta * Math.sqrt(p1 * (1 - p1) + p2 * (1 - p2))) ** 2;
  const denominator = (p2 - p1) ** 2;
  if (denominator === 0) return 0;
  return Math.ceil(numerator / denominator);
}

/**
 * Power analysis
 * Returns achieved power given current sample sizes
 */
export function calculatePower(convA, impA, convB, impB, confidence = 95) {
  if (impA === 0 || impB === 0) return 0;
  const pA = convA / impA;
  const pB = convB / impB;
  const pPool = (convA + convB) / (impA + impB);
  if (pPool === 0 || pPool === 1) return 0;
  const zAlpha = confidence === 99 ? 2.576 : confidence === 95 ? 1.96 : 1.645;
  const se = Math.sqrt(pPool * (1 - pPool) * (1 / impA + 1 / impB));
  if (se === 0) return 0;
  const z = Math.abs(pB - pA) / se;
  const zBeta = z - zAlpha;
  const power = normalCDF(zBeta) * 100;
  return Math.min(99, Math.max(0, Math.round(power)));
}

function generateTimeSeries(baseRate, impressions, days) {
  const data = [];
  for (let i = 0; i < days; i++) {
    const dailyImpressions = Math.round(impressions / days * (0.7 + Math.random() * 0.6));
    const rateVariation = baseRate * (0.8 + Math.random() * 0.4);
    const conversions = Math.round(dailyImpressions * rateVariation);
    data.push({
      date: new Date(now - (days - i) * day).toISOString().split('T')[0],
      impressions: dailyImpressions,
      conversions,
    });
  }
  return data;
}

// ─── Test Templates ───

export const testTemplates = [
  {
    id: 'tpl-cta-color',
    name: 'CTA Button Color',
    description: 'Test different CTA button colors to maximize click-through rate. Best for landing pages and pricing pages.',
    type: 'page',
    recommendedMetric: 'conversion_rate',
    recommendedDuration: 14,
    recommendedSplit: { a: 50, b: 50 },
    variantA: { name: 'Control', ctaColor: 'amber', ctaText: 'Get Started' },
    variantB: { name: 'Variation', ctaColor: 'emerald', ctaText: 'Get Started' },
    hypothesis: 'Changing the CTA button color from amber to emerald will increase click-through rate by at least 10%.',
  },
  {
    id: 'tpl-headline',
    name: 'Headline Copy',
    description: 'Test different headline copy variations to improve engagement and signup rates. Ideal for homepage and landing pages.',
    type: 'page',
    recommendedMetric: 'conversion_rate',
    recommendedDuration: 14,
    recommendedSplit: { a: 50, b: 50 },
    variantA: { name: 'Control', headline: 'Build AI Agents That Work 24/7' },
    variantB: { name: 'Variation', headline: 'Deploy Your First AI Worker in 5 Minutes' },
    hypothesis: 'A benefit-driven headline will outperform a feature-driven headline in signup conversion.',
  },
  {
    id: 'tpl-pricing-layout',
    name: 'Pricing Page Layout',
    description: 'Test different pricing page layouts (3-column vs 4-column, highlighted plan position) to optimize plan selection.',
    type: 'page',
    recommendedMetric: 'conversion_rate',
    recommendedDuration: 21,
    recommendedSplit: { a: 50, b: 50 },
    variantA: { name: 'Control', layout: '3-column', highlighted: 'center' },
    variantB: { name: 'Variation', layout: '3-column', highlighted: 'left' },
    hypothesis: 'Highlighting the leftmost (cheapest) plan will increase overall conversion by driving more starter plan signups.',
  },
  {
    id: 'tpl-onboarding',
    name: 'Onboarding Flow',
    description: 'Test different onboarding flow lengths and steps to improve activation rate and time-to-value.',
    type: 'flow',
    recommendedMetric: 'engagement',
    recommendedDuration: 21,
    recommendedSplit: { a: 50, b: 50 },
    variantA: { name: 'Control', steps: 5, skippable: false },
    variantB: { name: 'Variation', steps: 3, skippable: true },
    hypothesis: 'A shorter, skippable onboarding flow will increase activation rate by reducing friction.',
  },
  {
    id: 'tpl-email-subject',
    name: 'Email Subject Line',
    description: 'Test different email subject line styles to improve open rates and click-through rates.',
    type: 'message',
    recommendedMetric: 'engagement',
    recommendedDuration: 7,
    recommendedSplit: { a: 50, b: 50 },
    variantA: { name: 'Control', subject: 'Welcome to Max OS Cloud', tone: 'formal' },
    variantB: { name: 'Variation', subject: 'Your AI journey starts now — here is your roadmap', tone: 'friendly' },
    hypothesis: 'A personalized, friendly subject line will increase open rates by 15% compared to a formal one.',
  },
];

// ─── Default Mock Data ───

const defaultTests = [
  {
    id: 'ab-001',
    name: 'Homepage Hero Headline',
    type: 'page',
    status: 'running',
    description: 'Testing two hero headline variants to improve signup conversion',
    variantA: { name: 'Control', headline: 'Build AI Agents That Work 24/7', cta: 'Get Started' },
    variantB: { name: 'Variation', headline: 'Deploy Your First AI Worker in 5 Minutes', cta: 'Start Free Trial' },
    splitRatio: { a: 50, b: 50 },
    successMetric: 'conversion_rate',
    secondaryMetric: 'engagement',
    estimatedDurationDays: 14,
    audiencePercentage: 100,
    audienceSegments: [],
    startedAt: now - 7 * day,
    impressions: { a: 4520, b: 4480 },
    conversions: { a: 312, b: 401 },
    timeSeriesA: generateTimeSeries(0.069, 4520, 7),
    timeSeriesB: generateTimeSeries(0.089, 4480, 7),
  },
  {
    id: 'ab-002',
    name: 'Agent Welcome Message',
    type: 'message',
    status: 'running',
    description: 'Testing personalized vs generic welcome message for new agent onboarding',
    variantA: { name: 'Control', message: 'Hello! I am your AI assistant. How can I help you today?' },
    variantB: { name: 'Variation', message: 'Hey there! I am {agent_name}, your dedicated AI agent. I can help with tasks, answer questions, and automate your workflow. What would you like to start with?' },
    splitRatio: { a: 50, b: 50 },
    successMetric: 'response_time',
    secondaryMetric: 'engagement',
    estimatedDurationDays: 10,
    audiencePercentage: 100,
    audienceSegments: [],
    startedAt: now - 4 * day,
    impressions: { a: 2150, b: 2180 },
    conversions: { a: 860, b: 1134 },
    timeSeriesA: generateTimeSeries(0.40, 2150, 4),
    timeSeriesB: generateTimeSeries(0.52, 2180, 4),
  },
  {
    id: 'ab-003',
    name: 'Checkout Flow Simplification',
    type: 'flow',
    status: 'completed',
    description: 'Testing single-page checkout vs multi-step checkout flow',
    variantA: { name: 'Control', steps: ['Cart', 'Shipping', 'Payment', 'Review', 'Confirm'] },
    variantB: { name: 'Variation', steps: ['Cart + Payment', 'Confirm'] },
    splitRatio: { a: 50, b: 50 },
    successMetric: 'conversion_rate',
    secondaryMetric: 'retention',
    estimatedDurationDays: 21,
    audiencePercentage: 100,
    audienceSegments: [],
    startedAt: now - 25 * day,
    completedAt: now - 4 * day,
    impressions: { a: 8900, b: 8850 },
    conversions: { a: 1780, b: 2212 },
    timeSeriesA: generateTimeSeries(0.20, 8900, 21),
    timeSeriesB: generateTimeSeries(0.25, 8850, 21),
    winner: 'b',
  },
  {
    id: 'ab-004',
    name: 'Pricing Page CTA Color',
    type: 'page',
    status: 'paused',
    description: 'Testing amber vs emerald CTA button on pricing page',
    variantA: { name: 'Control', ctaColor: 'amber', ctaText: 'Choose Plan' },
    variantB: { name: 'Variation', ctaColor: 'emerald', ctaText: 'Choose Plan' },
    splitRatio: { a: 70, b: 30 },
    successMetric: 'conversion_rate',
    secondaryMetric: 'engagement',
    estimatedDurationDays: 7,
    audiencePercentage: 100,
    audienceSegments: [],
    startedAt: now - 10 * day,
    pausedAt: now - 3 * day,
    impressions: { a: 3200, b: 1350 },
    conversions: { a: 224, b: 108 },
    timeSeriesA: generateTimeSeries(0.07, 3200, 7),
    timeSeriesB: generateTimeSeries(0.08, 1350, 7),
  },
  {
    id: 'ab-005',
    name: 'Onboarding Email Sequence',
    type: 'message',
    status: 'completed',
    description: 'Testing 3-email vs 5-email onboarding sequence for activation rate',
    variantA: { name: 'Control', emails: 3, subject: 'Welcome to Max OS Cloud' },
    variantB: { name: 'Variation', emails: 5, subject: 'Your AI journey starts now — here is your roadmap' },
    splitRatio: { a: 50, b: 50 },
    successMetric: 'conversion_rate',
    secondaryMetric: 'retention',
    estimatedDurationDays: 30,
    audiencePercentage: 100,
    audienceSegments: [],
    startedAt: now - 45 * day,
    completedAt: now - 15 * day,
    impressions: { a: 5600, b: 5580 },
    conversions: { a: 1400, b: 1786 },
    timeSeriesA: generateTimeSeries(0.25, 5600, 30),
    timeSeriesB: generateTimeSeries(0.32, 5580, 30),
    winner: 'b',
  },
  {
    id: 'ab-006',
    name: 'Dashboard Widget Layout',
    type: 'page',
    status: 'running',
    description: 'Testing grid layout vs list layout for dashboard widgets',
    variantA: { name: 'Control', layout: 'grid', columns: 3 },
    variantB: { name: 'Variation', layout: 'list', columns: 1 },
    splitRatio: { a: 50, b: 50 },
    successMetric: 'response_time',
    secondaryMetric: 'engagement',
    estimatedDurationDays: 14,
    audiencePercentage: 100,
    audienceSegments: [],
    startedAt: now - 2 * day,
    impressions: { a: 890, b: 910 },
    conversions: { a: 445, b: 491 },
    timeSeriesA: generateTimeSeries(0.50, 890, 2),
    timeSeriesB: generateTimeSeries(0.54, 910, 2),
  },
];

// ─── LocalStorage Persistence ───

function loadFromStorage() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed) && parsed.length > 0) return parsed;
    }
  } catch {}
  return null;
}

function saveToStorage(tests) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(tests));
  } catch {}
}

// In-memory store (synced with localStorage)
let abTests = loadFromStorage() || [...defaultTests];

// ─── CRUD Operations ───

export function getEnrichedTests() {
  return abTests.map(test => enrichTest(test));
}

function enrichTest(test) {
  const { impressions, conversions } = test;
  const rateA = impressions.a > 0 ? conversions.a / impressions.a : 0;
  const rateB = impressions.b > 0 ? conversions.b / impressions.b : 0;
  const confidence = calculateConfidence(conversions.a, impressions.a, conversions.b, impressions.b);
  const pValue = calculatePValue(conversions.a, impressions.a, conversions.b, impressions.b);
  const lift = rateA > 0 ? ((rateB - rateA) / rateA) * 100 : 0;
  const power = calculatePower(conversions.a, impressions.a, conversions.b, impressions.b);

  let winner = null;
  if (test.status === 'completed' && test.winner) {
    winner = test.winner;
  } else if (confidence >= 95) {
    winner = rateB > rateA ? 'b' : 'a';
  }

  return {
    ...test,
    stats: {
      rateA: Math.round(rateA * 10000) / 100,
      rateB: Math.round(rateB * 10000) / 100,
      confidence,
      pValue,
      lift: Math.round(lift * 10) / 10,
      power,
      winner,
      totalImpressions: impressions.a + impressions.b,
      totalConversions: conversions.a + conversions.b,
    },
  };
}

export function getTestById(id) {
  const test = abTests.find(t => t.id === id);
  if (!test) return null;
  return enrichTest(test);
}

export function createTest(testData) {
  const newTest = {
    ...testData,
    id: `ab-${String(abTests.length + 1).padStart(3, '0')}`,
    status: 'draft',
    startedAt: null,
    completedAt: null,
    impressions: { a: 0, b: 0 },
    conversions: { a: 0, b: 0 },
    timeSeriesA: [],
    timeSeriesB: [],
  };
  abTests.push(newTest);
  saveToStorage(abTests);
  return newTest;
}

export function updateTest(id, updates) {
  const idx = abTests.findIndex(t => t.id === id);
  if (idx === -1) return null;
  abTests[idx] = { ...abTests[idx], ...updates };
  saveToStorage(abTests);
  return abTests[idx];
}

export function deleteTest(id) {
  const idx = abTests.findIndex(t => t.id === id);
  if (idx === -1) return false;
  abTests.splice(idx, 1);
  saveToStorage(abTests);
  return true;
}

export function startTest(id) {
  return updateTest(id, { status: 'running', startedAt: Date.now() });
}

export function pauseTest(id) {
  return updateTest(id, { status: 'paused', pausedAt: Date.now() });
}

export function resumeTest(id) {
  return updateTest(id, { status: 'running', resumedAt: Date.now() });
}

export function completeTest(id, winner) {
  return updateTest(id, { status: 'completed', completedAt: Date.now(), winner });
}

export function archiveTest(id) {
  return updateTest(id, { status: 'archived', archivedAt: Date.now() });
}

export function resetToDefaults() {
  abTests = [...defaultTests];
  saveToStorage(abTests);
}

// ─── Export Helpers ───

export function exportToCSV(test) {
  const { stats } = test;
  const headers = ['Variant', 'Impressions', 'Conversions', 'Conversion Rate', 'Lift'];
  const rows = [
    ['A (Control)', test.impressions.a, test.conversions.a, `${stats.rateA}%`, '—'],
    ['B (Variation)', test.impressions.b, test.conversions.b, `${stats.rateB}%`, `${stats.lift > 0 ? '+' : ''}${stats.lift}%`],
  ];
  const summary = [
    [],
    ['Confidence', `${stats.confidence}%`],
    ['P-Value', stats.pValue],
    ['Power', `${stats.power}%`],
    ['Winner', stats.winner ? `Variant ${stats.winner.toUpperCase()}` : 'None yet'],
  ];

  const csvContent = [headers, ...rows, ...summary].map(row => row.join(',')).join('\n');
  return csvContent;
}

export function exportToJSON(test) {
  return JSON.stringify({
    id: test.id,
    name: test.name,
    type: test.type,
    status: test.status,
    description: test.description,
    variantA: test.variantA,
    variantB: test.variantB,
    splitRatio: test.splitRatio,
    successMetric: test.successMetric,
    stats: test.stats,
    impressions: test.impressions,
    conversions: test.conversions,
    startedAt: test.startedAt,
    completedAt: test.completedAt,
    winner: test.winner || null,
  }, null, 2);
}

// ─── Configuration Constants ───

export const testTypes = [
  { value: 'page', label: 'Page Test', description: 'Test page variations (headline, CTA, layout)' },
  { value: 'message', label: 'Message Test', description: 'Test agent message variations' },
  { value: 'flow', label: 'Flow Test', description: 'Test automation flow variations' },
];

export const successMetrics = [
  { value: 'conversion_rate', label: 'Conversion Rate' },
  { value: 'response_time', label: 'Response Time' },
  { value: 'engagement', label: 'Engagement' },
  { value: 'retention', label: 'Retention' },
];

export const splitRatioOptions = [
  { label: '50 / 50', a: 50, b: 50 },
  { label: '70 / 30', a: 70, b: 30 },
  { label: '80 / 20', a: 80, b: 20 },
  { label: '60 / 40', a: 60, b: 40 },
];

export const statusOptions = [
  { value: 'draft', label: 'Draft' },
  { value: 'running', label: 'Running' },
  { value: 'paused', label: 'Paused' },
  { value: 'completed', label: 'Completed' },
  { value: 'archived', label: 'Archived' },
];

export const confidenceLevels = [
  { value: 90, label: '90%' },
  { value: 95, label: '95%' },
  { value: 99, label: '99%' },
];

export const audienceSegments = [
  { value: 'all', label: 'All Users' },
  { value: 'new', label: 'New Users' },
  { value: 'returning', label: 'Returning Users' },
  { value: 'enterprise', label: 'Enterprise Plan' },
  { value: 'starter', label: 'Starter Plan' },
];
